<template>
  <div class="ue-copy-to-clipboard">
    <slot />
    <button
      type="button"
      :id="`copy-button-${id}`"
      :class="['btn', `btn-${props.variant}`, `btn-${props.size}`, buttonClass]"
      @click="copy"
      :aria-label="copied ? 'Copied!' : 'Copy to clipboard'"
    >
      <i :class="copied ? 'bi bi-check-lg copied' : 'bi bi-copy'" />
      {{ label || '' }}
    </button>

    <UeTooltip v-if="tooltip" :target="`copy-button-${id}`">{{
      copied ? 'Copied!' : 'Copy to clipboard'
    }}</UeTooltip>
  </div>
</template>

<script setup lang="ts">
import UeTooltip from '@components/base/UeTooltip.vue'
import { useSlots, computed, onUnmounted, ref } from 'vue'
import { copyToClipboard } from '@lib/copyToClipboard'
import randomHash from '@lib/random-hash'

interface Props {
  /** The text content that will be copied to the clipboard */
  text: string
  /** Optional label text to display on the button (when used as standalone) */
  label?: string
  /** Button variant style - controls the visual appearance */
  variant?: 'link' | 'primary' | 'secondary' | 'light' | 'dark'
  /** Button size - only applies when used as standalone button */
  size?: 'sm' | 'md' | 'lg'
  /** Whether to show a tooltip on hover with copy/copied status */
  tooltip?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  variant: 'light',
  size: 'sm',
  tooltip: true,
})

const emit = defineEmits<{
  (e: 'copied', value: string): void
}>()

const slots = useSlots()
const copied = ref(false)
const copyTimeoutRef = ref<ReturnType<typeof setTimeout> | null>(null)
const id = randomHash()

const hasChild = computed(() => {
  return slots.default !== undefined
})

const buttonClass = computed(() => {
  return hasChild.value ? 'copy-button' : ''
})

const copy = (): void => {
  copyToClipboard(props.text)
  copied.value = true
  emit('copied', props.text)

  if (copyTimeoutRef.value) {
    clearTimeout(copyTimeoutRef.value)
  }

  copyTimeoutRef.value = setTimeout(() => {
    copied.value = false
    copyTimeoutRef.value = null
  }, 3000)
}

onUnmounted(() => {
  if (copyTimeoutRef.value) {
    clearTimeout(copyTimeoutRef.value)
  }
})
</script>

<style lang="sass" scoped>
.ue-copy-to-clipboard
  position: relative
  display: inline-block
  width: 100%

  .copy-button
    position: absolute
    right: 8px
    top: 50%
    transform: translateY(-50%)

    &.textarea-position
      top: 8px
      transform: none

    i
      &.copied
        color: var(--primary)
</style>
